import { type FC, type ReactNode, createContext, useContext, useState, useCallback } from 'react';

import { AlertDialog, AlertDialogVariant } from './AlertDialog';
import { ConfirmDialog, ConfirmDialogVariant } from './ConfirmDialog';

/**
 * Options for the confirm dialog
 */
export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
}

/**
 * Options for the alert dialog
 */
export interface AlertOptions {
  title: string;
  message: ReactNode;
  okText?: string;
  variant?: AlertDialogVariant;
}

/**
 * Dialog context value
 */
interface DialogContextValue {
  /**
   * Show a confirmation dialog and return a promise that resolves to true if confirmed
   */
  confirm: (options: ConfirmOptions) => Promise<boolean>;

  /**
   * Show an alert dialog and return a promise that resolves when closed
   */
  alert: (options: AlertOptions) => Promise<void>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

interface AlertState extends AlertOptions {
  resolve: () => void;
}

/**
 * DialogProvider component
 *
 * Provides a context for showing confirm and alert dialogs imperatively.
 * Wrap your app with this provider to use the useDialog hook.
 *
 * @example
 * ```tsx
 * // In your app root
 * <DialogProvider>
 *   <App />
 * </DialogProvider>
 *
 * // In any component
 * const { confirm, alert } = useDialog();
 *
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     variant: 'danger',
 *   });
 *   if (confirmed) {
 *     // perform delete
 *   }
 * };
 * ```
 */
export const DialogProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [alertState, setAlertState] = useState<AlertState | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({ ...options, resolve });
    });
  }, []);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({ ...options, resolve });
    });
  }, []);

  const handleConfirmClose = useCallback(() => {
    if (confirmState) {
      confirmState.resolve(false);
      setConfirmState(null);
    }
  }, [confirmState]);

  const handleConfirm = useCallback(() => {
    if (confirmState) {
      confirmState.resolve(true);
      setConfirmState(null);
    }
  }, [confirmState]);

  const handleAlertClose = useCallback(() => {
    if (alertState) {
      alertState.resolve();
      setAlertState(null);
    }
  }, [alertState]);

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState !== null}
        onClose={handleConfirmClose}
        onConfirm={handleConfirm}
        title={confirmState?.title || ''}
        message={confirmState?.message || ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertState !== null}
        onClose={handleAlertClose}
        title={alertState?.title || ''}
        message={alertState?.message || ''}
        okText={alertState?.okText}
        variant={alertState?.variant}
      />
    </DialogContext.Provider>
  );
};

DialogProvider.displayName = 'DialogProvider';

/**
 * useDialog hook
 *
 * Returns functions to show confirm and alert dialogs imperatively.
 *
 * @example
 * ```tsx
 * const { confirm, alert } = useDialog();
 *
 * // Confirmation dialog
 * const confirmed = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?',
 *   variant: 'danger',
 *   confirmText: 'Delete',
 * });
 *
 * // Alert dialog
 * await alert({
 *   title: 'Error',
 *   message: 'Please fill in all required fields.',
 *   variant: 'error',
 * });
 * ```
 */
export function useDialog(): DialogContextValue {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }

  return context;
}
